import type { Session } from '@core/session/types';
import { findMenuDestination, resolveMenu, type ResolvedMenuNode } from '@/shell/menu';

/**
 * Where a person lands, and what to do when the key they arrived on is not theirs to see.
 *
 * <p>
 * {@link HOME_KEY} is a constant — `branch`, a Setup master — because when it was written there was
 * no dashboard for it to mean instead. A constant cannot know who is signed in, so a user without
 * `Branch:View` was sent to the branches screen and shown a permission error by the very first
 * thing the product rendered for them. The sidebar never offered that row; only the landing did.
 * </p>
 *
 * <p>
 * <strong>Being unable to see a screen and asking for one that does not exist are different
 * mistakes, and they are answered differently.</strong> A key the manifest declares but this person
 * may not see is the product's fault and is redirected. A key nothing has heard of is a mistyped or
 * stale address and keeps its 404 — `location.ts` says exactly this about {@link NOT_FOUND_KEY}:
 * answering with a real screen instead "would quietly rewrite a mistyped URL into the branches
 * screen and tell the user their link was fine". So {@link isMisdirected} asks both questions and
 * only reports the first.
 * </p>
 */

/**
 * The first screen this person can actually use, or null when there is none.
 *
 * <p>
 * Preference order is deliberate. A <em>built, unlocked</em> leaf is a working screen. A locked one
 * renders an upgrade prompt and a pending one renders coming-soon — both are honest answers and
 * neither is a landing worth choosing, so they are taken only when nothing better exists. Null is
 * the last of those answers rather than a failure: a user who can reach nothing at all should be
 * told so by the 404, not looped back into a screen that will refuse them again.
 * </p>
 *
 * <p>
 * It reads {@link resolveMenu}, so it can never offer something the sidebar would not — the two
 * cannot drift, because there is only one filter and this asks it the same question the sidebar
 * does.
 * </p>
 */
export function landingKey(session: Session): string | null {
  const visible = resolveMenu(session);

  return (
    firstLeaf(visible, (leaf) => !leaf.locked && !leaf.pending) ?? firstLeaf(visible, () => true)
  );
}

/**
 * Whether `key` is a screen the product declares and this person may not see.
 *
 * <p>
 * True is the one case worth redirecting: they asked for something real and were refused. False
 * covers both of the others — a screen they may see, and a key nothing declares — because neither
 * wants moving. The caller is an effect rather than `screenFor`, so the redirect changes the active
 * key and the URL together; rendering a different screen under the old key would leave the sidebar
 * highlighting a row the content does not match.
 * </p>
 */
export function isMisdirected(session: Session, key: string): boolean {
  if (findMenuDestination(key) === null) {
    return false;
  }

  return !declares(resolveMenu(session), key);
}

/**
 * Whether the resolved tree holds `key` anywhere, groups included.
 *
 * <p>
 * Deliberately not {@link firstLeaf} with a key test. `findMenuDestination` matches a group heading
 * as readily as a leaf, and `navigate` accepts one — it "moves the screen and leaves the URL alone".
 * A leaf-only search would find no such row and report a group this person can plainly see as
 * misdirected, redirecting them off it.
 * </p>
 */
function declares(nodes: ResolvedMenuNode[], key: string): boolean {
  return nodes.some(
    (node) => node.key === key || (node.children ? declares(node.children, key) : false),
  );
}

/**
 * Depth-first, so the answer follows the order the sidebar renders rather than a flattened one.
 *
 * <p>
 * Group headings are skipped by the `path` test rather than by looking for children: a group is a
 * heading and not a destination, and one carrying a path would be a manifest error worth landing on
 * rather than silently stepping over.
 * </p>
 */
function firstLeaf(
  nodes: ResolvedMenuNode[],
  accept: (leaf: ResolvedMenuNode) => boolean,
): string | null {
  for (const node of nodes) {
    if (node.children) {
      const found = firstLeaf(node.children, accept);

      if (found !== null) {
        return found;
      }

      continue;
    }

    if (node.path !== undefined && accept(node)) {
      return node.key;
    }
  }

  return null;
}
